/* ⚛ REACT */
import { useRef } from "react";

/* 📦 LIBS */
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

/* 🧩 COMPONENTS */
import { InputRadio, Button, SaveIcon } from "../../../Components";

/* 🎨 STYLES */
import { UnitsContainer, Title } from "./styles";
import { store } from "../../../Redux/Store";
import { changeModal } from "../../../Redux/Actions";

const UnitsTab = ({ userId, Services, usuario, setUsuario }) => {
    const { t } = useTranslation();

    const flowRateOptions = [
        {id: 'L/s', label: "L/s", help: t("administrative.sub-items.employees.view.measurement-units.help-text.flowrate.l/s")},
        {id: 'L/h', label: "L/h", help: t("administrative.sub-items.employees.view.measurement-units.help-text.flowrate.l/h")},
        {id: 'mL/min', label: "mL/min", help: t("administrative.sub-items.employees.view.measurement-units.help-text.flowrate.ml/min")},
        {id: 'm³/h', label: "m³/h", help: t("administrative.sub-items.employees.view.measurement-units.help-text.flowrate.m3/h")},
        {id: 'GPM', label: "GPM", help: t("administrative.sub-items.employees.view.measurement-units.help-text.flowrate.gpm")},
        {id: 'GPD', label: "GPD", help: t("administrative.sub-items.employees.view.measurement-units.help-text.flowrate.gpd")},
    ];

    const pressureOptions = [
        {id: 'bar', label: "bar", help: t("administrative.sub-items.employees.view.measurement-units.help-text.pressure.bar")},
        {id: 'kgf/cm²', label: "kgf/cm²", help: t("administrative.sub-items.employees.view.measurement-units.help-text.pressure.kgf/cm2")},
        {id: 'Pa', label: "Pa", help: t("administrative.sub-items.employees.view.measurement-units.help-text.pressure.pa")},
        {id: 'mPa', label: "mPa", help: t("administrative.sub-items.employees.view.measurement-units.help-text.pressure.mpa")},
        {id: 'psi', label: "psi", help: t("administrative.sub-items.employees.view.measurement-units.help-text.pressure.psi")},
    ];

    const conductivityOptions = [
        {id: 'µS/cm', label: "µS/cm", help: t("administrative.sub-items.employees.view.measurement-units.help-text.conductivity.us/cm")},
        {id: 'mS/cm', label: "mS/cm", help: t("administrative.sub-items.employees.view.measurement-units.help-text.conductivity.ms/cm")},
        {id: 'S/cm', label: "S/cm", help: t("administrative.sub-items.employees.view.measurement-units.help-text.conductivity.s/m")},
    ];

    const massOptions = [
        {id: 't', label: "t", help: t("administrative.sub-items.employees.view.measurement-units.help-text.mass.t")},
        {id: 'kg', label: "kg", help: t("administrative.sub-items.employees.view.measurement-units.help-text.mass.kg")},
        {id: 'g', label: "g", help: t("administrative.sub-items.employees.view.measurement-units.help-text.mass.g")},
        {id: 'mg', label: "mg", help: t("administrative.sub-items.employees.view.measurement-units.help-text.mass.mg")},
        {id: 'lb', label: "lb", help: t("administrative.sub-items.employees.view.measurement-units.help-text.mass.lb")},
        {id: 'oz', label: "oz", help: t("administrative.sub-items.employees.view.measurement-units.help-text.mass.oz")},
    ];

    const levelOptions = [
        {id: 'm', label: "m", help: t("administrative.sub-items.employees.view.measurement-units.help-text.level.m")},
        {id: 'cm', label: "cm", help: t("administrative.sub-items.employees.view.measurement-units.help-text.level.cm")},
        {id: 'mm', label: "mm", help: t("administrative.sub-items.employees.view.measurement-units.help-text.level.mm")},
        {id: 'ft', label: "ft", help: t("administrative.sub-items.employees.view.measurement-units.help-text.level.ft")},
        {id: 'in', label: "in", help: t("administrative.sub-items.employees.view.measurement-units.help-text.level.in")},
    ];

    const volumeOptions = [
        {id: 'm³', label: "m³", help: ("administrative.sub-items.employees.view.measurement-units.help-text.volume.m3")},
        {id: 'L', label: "L", help: t("administrative.sub-items.employees.view.measurement-units.help-text.volume.l")},
        {id: 'mL', label: "mL", help: t("administrative.sub-items.employees.view.measurement-units.help-text.volume.ml")},
        {id: 'gal', label: "gal", help: t("administrative.sub-items.employees.view.measurement-units.help-text.volume.gal")},
    ];

    const solidsOptions = [
        {id: 'ppm', label: "ppm", help: t("administrative.sub-items.employees.view.measurement-units.help-text.solids.ppm")},
        {id: 'mg/L', label: "mg/L", help: t("administrative.sub-items.employees.view.measurement-units.help-text.solids.mg/l")},
    ];

    const fluorideOptions = [
        {id: 'ppm', label: "ppm", help: t("administrative.sub-items.employees.view.measurement-units.help-text.fluoride.ppm")},
        {id: 'mg/L', label: "mg/L", help: t("administrative.sub-items.employees.view.measurement-units.help-text.fluoride.mg/l")},
    ];

    const turbidityOptions = [
        {id: 'NTU', label: "NTU", help: t("administrative.sub-items.employees.view.measurement-units.help-text.turbidity.ntu")},
    ];

    const temperatureOptions = [
        {id: 'c', label: "°C", help: t("administrative.sub-items.employees.view.measurement-units.help-text.temperature.c")},
        {id: 'f', label: "°F", help: t("administrative.sub-items.employees.view.measurement-units.help-text.temperature.f")},
        {id: 'k', label: "K", help: t("administrative.sub-items.employees.view.measurement-units.help-text.temperature.k")},
    ];

    const useUnitRef = (options, userValue, fallbackIndex = 0) => {
        const defaultId = options[fallbackIndex]?.id;
        const savedId = options.find(option => option.id === userValue)?.id;
        return useRef(savedId ?? defaultId);
    };

    const flowRate = useUnitRef(flowRateOptions, usuario.units?.flowrate, 1);
    const pressure = useUnitRef(pressureOptions, usuario.units?.pressure, 0);
    const conductivity = useUnitRef(conductivityOptions, usuario.units?.conductivity, 0);
    const mass = useUnitRef(massOptions, usuario.units?.mass, 1);
    const level = useUnitRef(levelOptions, usuario.units?.level, 0);
    const volume = useUnitRef(volumeOptions, usuario.units?.volume, 0);
    const solids = useUnitRef(solidsOptions, usuario.units?.solids, 0);
    const fluoride = useUnitRef(fluorideOptions, usuario.units?.fluoride, 0);
    const turbidity = useUnitRef(turbidityOptions, usuario.units?.turbidity, 0);
    const temperature = useUnitRef(temperatureOptions, usuario.units?.temperature, 0);

    const SaveUnits = async() => {
        const units = {
            volume: volume.current,
            flowrate: flowRate.current,
            pressure: pressure.current,
            mass: mass.current,
            level: level.current,
            solids: solids.current,
            conductivity: conductivity.current,
            fluoride: fluoride.current,
            turbidity: turbidity.current,
            temperature: temperature.current,
        };

        const payload = { units };
        
        const isValid = Object.keys(units).every(key => {
            const value = units[key];
            const options = {
                'volume': volumeOptions,
                'flowrate': flowRateOptions,
                'pressure': pressureOptions,
                'mass': massOptions,
                'level': levelOptions,
                'solids': solidsOptions,
                'conductivity': conductivityOptions,
                'fluoride': fluorideOptions,
                'turbidity': turbidityOptions,
                'temperature': temperatureOptions
            }[key];
            return value !== null && value !== undefined && value !== '' && options.some(option => option.id === value);
        });
        
        if (isValid) {
           let response = await Services.current.editMeasurementUnits(userId, payload);
           console.log(response)
            
            setUsuario(prevState => ({
                ...prevState,
                units: units
            }));

            toast(t("message.user-service.edit-units"), {
                position: "top-center",
                type: 'success',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
            });

        } else {
            toast(t("message.invalid-units"), {
                position: "top-center",
                type: 'info',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
            });
        }
    };

    return (
        <UnitsContainer>
            <Title>{t("administrative.sub-items.employees.view.measurement-units.title")}</Title>
            <div className="units-information" >
                <div className="info-card">
                    <h3>{t("administrative.sub-items.employees.view.measurement-units.table.title")}</h3>
                    <table>
                        <thead>
                        <tr>
                            <th>{t("administrative.sub-items.employees.view.measurement-units.table.header.units")}</th>
                            <th>{t("administrative.sub-items.employees.view.measurement-units.table.header.english")}</th>
                            <th>{t("administrative.sub-items.employees.view.measurement-units.table.header.portuguese")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.flowrate")}:</td>
                            <td>mL/min</td>
                            <td>mL/min</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.pressure")}:</td>
                            <td>bar</td>
                            <td>bar</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.conductivity")}:</td>
                            <td>µS/cm</td>
                            <td>µS/cm</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.mass")}:</td>
                            <td>kg</td>
                            <td>kg</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.length")}:</td>
                            <td>m</td>
                            <td>m</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.height")}:</td>
                            <td>m</td>
                            <td>m</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.level")}:</td>
                            <td>m</td>
                            <td>m</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.volume")}:</td>
                            <td>mL</td>
                            <td>mL</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.solids")}:</td>
                            <td>ppm</td>
                            <td>ppm</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.free-chlorine")}:</td>
                            <td>ppm</td>
                            <td>ppm</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.total-chlorine")}:</td>
                            <td>ppm</td>
                            <td>ppm</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.fluoride")}:</td>
                            <td>ppm</td>
                            <td>ppm</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.turbidity")}:</td>
                            <td>NTU</td>
                            <td>NTU</td>
                        </tr>
                        <tr>
                            <td>{t("administrative.sub-items.employees.view.measurement-units.table.body.temperature")}:</td>
                            <td>°F</td>
                            <td>°C</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <div className="units-grid" id="select-unit">
                    <div className="card" >
                        <h4>{t("administrative.sub-items.employees.view.measurement-units.units-grid.flowrate")}:</h4>
                        <div className="options-wrapper">
                            <InputRadio name={'flowrate'} options={flowRateOptions} value={flowRate.current} 
                                onChange={(value) => {flowRate.current = value; return true}}/>
                        </div>
                    </div>

                    <div className="card">
                        <h4>{t("administrative.sub-items.employees.view.measurement-units.units-grid.pressure")}:</h4>
                        <div className="options-wrapper">
                            <InputRadio name={'pressure'} options={pressureOptions} value={pressure.current} 
                                onChange={(value) => {pressure.current = value; return true}}/>
                        </div>
                    </div>

                    <div className="card large">
                        <h4>{t("administrative.sub-items.employees.view.measurement-units.units-grid.conductivity")}:</h4>
                        <div className="options-wrapper">
                            <InputRadio name={'conductivity'} options={conductivityOptions} value={conductivity.current} 
                                onChange={(value) => {conductivity.current = value; return true}}/>
                        </div>
                    </div>

                    <div className="card">
                        <h4>{t("administrative.sub-items.employees.view.measurement-units.units-grid.mass")}:</h4>
                        <div className="options-wrapper">
                            <InputRadio name={'mass'} options={massOptions} value={mass.current} 
                                onChange={(value) => {mass.current = value; return true}}/>
                        </div>
                    </div>

                    <div className="card">
                        <h4>{t("administrative.sub-items.employees.view.measurement-units.units-grid.level")}:</h4>
                        <div className="options-wrapper">
                            <InputRadio name={'level'} options={levelOptions} value={level.current} 
                                onChange={(value) => {level.current = value; return true}}/>
                        </div>
                    </div>

                    <div className="card large">
                        <h4>{t("administrative.sub-items.employees.view.measurement-units.units-grid.volume")}:</h4>
                        <div className="options-wrapper">
                            <InputRadio name={'volume'} options={volumeOptions} value={volume.current} 
                                onChange={(value) => {volume.current = value; return true}}/>
                        </div>
                    </div>

                    <div className="card small">
                        <h4>{t("administrative.sub-items.employees.view.measurement-units.units-grid.solids")}:</h4>
                        <div className="options-wrapper">
                            <InputRadio name={'solids'} options={solidsOptions} value={solids.current} 
                                onChange={(value) => {solids.current = value; return true}}/>
                        </div>
                    </div>

                    <div className="card small">
                        <h4>{t("administrative.sub-items.employees.view.measurement-units.units-grid.fluoride")}:</h4>
                        <div className="options-wrapper">
                            <InputRadio name={'fluoride'} options={fluorideOptions} value={fluoride.current} 
                                onChange={(value) => {fluoride.current = value; return true}}/>
                        </div>
                    </div>

                    <div className="card small center">
                        <h4>{t("administrative.sub-items.employees.view.measurement-units.units-grid.turbidity")}:</h4>
                        <div className="options-wrapper">
                            <InputRadio name={'turbidity'} options={turbidityOptions} value={turbidity.current} 
                                onChange={(value) => {turbidity.current = value; return true}}/>
                        </div>
                    </div>

                    <div className="card large">
                        <h4>{t("administrative.sub-items.employees.view.measurement-units.units-grid.temperature")}:</h4>
                        <div className="options-wrapper">
                            <InputRadio name={'temperature'} options={temperatureOptions} value={temperature.current} 
                                onChange={(value) => {temperature.current = value; return true}}/>
                        </div>
                    </div>
                </div>
            </div>
            <div className="button-container">
                <Button text={t("buttons.save")} width={'150px'} icon={<SaveIcon/>} onClick={() => SaveUnits()}/>
                <Button text={t("buttons.reset")} width={'150px'} color={'white-red'} onClick={() => store.dispatch(changeModal('resetUnits'))}/>
            </div>
        </UnitsContainer>
    );
};

export default UnitsTab;